import { IonCol, IonRow } from '@ionic/react';
import React, {
  MutableRefObject, ReactChild, useEffect, useRef, useState,
} from 'react';
import { Network } from '@capacitor/network';
import useClasses from '../../components/styles';
import { Category } from '../../interfaces/Meta';
import CategoryItem from './CategoryItem';

interface CategoryProps {
  categories: Category[];
}

const Categories: React.FunctionComponent<CategoryProps> = ({
  categories,
}: CategoryProps) => {
  const classes = useClasses();
  const wrapperRef = useRef() as MutableRefObject<HTMLDivElement>;
  const [currentWidth, setCurrentWidth] = useState(0);
  const [networkStatus, setNetworkStatus] = useState<boolean | undefined>(undefined);

  let columns: ReactChild[] = [];
  const rows: ReactChild[] = [];

  useEffect(() => {
    // Never let a rejected/unavailable plugin leave networkStatus undefined
    // forever - that alone would keep the grid permanently empty.
    Network.getStatus()
      .then((status) => setNetworkStatus(status.connected))
      .catch(() => setNetworkStatus(true));

    const el = wrapperRef.current;
    if (!el) return undefined;

    const measure = (): void => {
      const { width } = el.getBoundingClientRect();
      // Ionic hydrates ion-split-pane/ion-menu/ion-content asynchronously, so
      // the first measurement after mount is often 0. Keep the last good value
      // and pick up the real one as soon as the element is actually laid out.
      setCurrentWidth((prev) => (width > 0 ? width : prev));
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    window.addEventListener('resize', measure);

    return (): void => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  if (currentWidth > 0 && networkStatus !== undefined) {
    let itemsPerRows = 3;
    if (currentWidth < 285) {
      itemsPerRows = 2;
    }
    if (!networkStatus) {
      const categoriesStored = localStorage.getItem('categories');
      if (categoriesStored) {
        categories = JSON.parse(categoriesStored);
      }
    }
    categories
      .sort((a, b) => a.order - b.order)
      .forEach((c) => {
        if (c.show !== 'N') {
          columns.push(
            <IonCol key={columns.length} className={classes.categoryColumn}>
              <CategoryItem category={c} disabled={!networkStatus && c.slug !== 'crisis'} />
            </IonCol>,
          );
          if (columns.length === itemsPerRows) {
            rows.push(
              <IonRow key={rows.length} className={classes.row}>
                {columns}
              </IonRow>,
            );
            columns = [];
          }
        }
      });
  }

  return <div ref={wrapperRef}>{rows}</div>;
};

export default Categories;
